<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { getSafeRedirectPath } from '@/utils/auth/validation'

import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { FormCreateOrg } from '@/components/forms'
import { Spinner } from '@/components/ui/spinner'
import { ChevronRight } from 'lucide-vue-next'
import { SkylabPorthole } from '@/layouts'

const store = useStore()
const router = useRouter()
const route = useRoute()

const firebaseUser = computed(() => store.getters['user/getUser'])
const member = computed(() => store.getters['user/getMember'])
const organizations = computed(() => store.getters['org/getOrgs'])
const projectInvites = computed(() => store.getters['project/getAllInvites'])
const orgInvites = computed(() => store.getters['org/getPendingInvites'])

const allInvites = computed(() => {
  const project = projectInvites.value.map(i => ({
    key: `project-${i.uuid}`,
    type: 'project',
    typeLabel: 'Project',
    name: i.project_name,
    raw: i
  }))
  const org = orgInvites.value.map(i => ({
    key: `org-${i.id}`,
    type: 'org',
    typeLabel: 'Organization',
    name: i.organization_name,
    raw: i
  }))
  return [...project, ...org]
})
const isLoading = ref(false)
const dataReady = ref(false)
const cardVisible = ref(false)
const useExpandAnimation = ref(false)

const userAvatar = computed(() => {
  const url = member.value?.avatar_url || firebaseUser.value?.photoURL || null
  console.log('[avatar]', { avatar_url: member.value?.avatar_url, photoURL: firebaseUser.value?.photoURL, resolved: url })
  return url
})
const userName = computed(() => member.value?.display_name || firebaseUser.value?.displayName || null)
const userEmail = computed(() => member.value?.email || firebaseUser.value?.email || 'Error')
const userInitial = computed(() => {
  const name = userName.value
  const email = userEmail.value

  if (name) {
    return name.charAt(0).toUpperCase()
  } else if (email) {
    return email.charAt(0).toUpperCase()
  }
  return 'V'
})

const firebaseToken = computed(() => store.getters.getFirebaseToken)

const isFirebaseReady = computed(() => {
  return firebaseUser.value !== null && firebaseToken.value !== null
})

const showCreateOrgForm = ref(false)
const displayOrganizations = computed(() => {
  return organizations.value.map(org => ({
    id: org.organization_id,
    name: org.organization_name,
    avatar: org.avatar_url || '🏢',
    role: org.current_member?.role
  }))
})

const handleCreateOrganization = () => {
  showCreateOrgForm.value = true
}

const handleFormSubmit = async (result) => {
  if (result.success) {
    showCreateOrgForm.value = false

    try {
      await store.dispatch('org/fetchOrgs')

      if (organizations.value.length > 0) {
        const firstOrg = organizations.value[0]
        router.push({ name: 'Organization', params: { org_id: firstOrg.organization_id } })
      } else {
        router.push({ name: 'Dashboard' })
      }
    } catch (error) {
      console.error('Failed to fetch organizations after creation:', error)
      showCreateOrgForm.value = false
    }
  }
}

onMounted(async () => {
  const waitForFirebase = () => {
    return new Promise((resolve) => {
      const checkFirebase = () => {
        if (isFirebaseReady.value) {
          resolve()
        } else {
          setTimeout(checkFirebase, 100)
        }
      }
      checkFirebase()
    })
  }

  setTimeout(() => { cardVisible.value = true }, 150)
  await waitForFirebase()
  const startTime = Date.now()
  const slowTimer = setTimeout(() => { isLoading.value = true }, 500)
  try {
    await store.dispatch('org/fetchPendingOrgInvites')
    await store.dispatch('project/fetchAllInvitesFirebase', { firebaseToken: firebaseToken.value })
    const elapsed = Date.now() - startTime
    console.log(`[AuthOverview] data fetch: ${elapsed}ms`)
    if (isLoading.value) {
      useExpandAnimation.value = true
      await nextTick()
    }
  } catch (error) {
    console.error('Failed to fetch data:', error)
  } finally {
    clearTimeout(slowTimer)
    isLoading.value = false
    dataReady.value = true
  }

  maybeAutoOpen()
})

function maybeAutoOpen() {
  if (allInvites.value.length === 0 && displayOrganizations.value.length === 1) {
    handleOpenOrganization(displayOrganizations.value[0])
  }
}


const handleCloseForm = () => {
  showCreateOrgForm.value = false
}

const handleJoinInvite = async (item) => {
  if (item.type === 'org') {
    await store.dispatch('org/acceptOrgInvite', item.raw.id)
    await store.dispatch('org/fetchPendingOrgInvites')
  } else {
    await store.dispatch('project/acceptInviteFirebase', {
      invite: item.raw,
      firebaseToken: firebaseToken.value
    })
    await store.dispatch('project/fetchAllInvitesFirebase', { firebaseToken: firebaseToken.value })
  }
  await store.dispatch('org/fetchOrgs')
  maybeAutoOpen()
}

const handleOpenOrganization = (org) => {
  store.dispatch('org/setSelectedOrg', org.id.toString())
  const redirectPath = getSafeRedirectPath(route.query.redirect)
  if (redirectPath) {
    router.push(redirectPath)
  } else {
    router.push({ name: 'Organization', params: { org_id: org.id } })
  }
}

const handleLogout = async () => {
  try {
    await store.dispatch('logout')
    router.push({ name: 'SignIn' })
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

const handleHome = () => {
  router.push({ name: 'Dashboard' })
}
</script>

<template>
  <SkylabPorthole>
    <div
      class="w-full max-w-[464px] bg-card rounded-2xl shadow-xl px-8 py-8 card-scroll"
      :class="isLoading ? 'max-h-[260px] overflow-hidden' : ['max-h-[calc(100vh-360px)] overflow-y-auto', useExpandAnimation ? 'transition-[max-height] duration-500 ease-out' : '']"
      :style="{ clipPath: cardVisible ? 'inset(0% round 1rem)' : 'inset(50% 0% round 1rem)', transition: 'clip-path 0.5s ease-out' }"
    >

        <!-- User identity -->
        <div class="flex items-center mb-5">
          <Avatar size="base" class="mr-3 bg-primary flex-shrink-0">
            <AvatarImage v-if="userAvatar" :src="userAvatar" alt="User Avatar" />
            <AvatarFallback class="text-white font-bold">{{ userInitial }}</AvatarFallback>
          </Avatar>
          <div class="text-sm min-w-0 flex-1">
            <strong v-if="userName" class="text-foreground">{{ userName }}</strong>
            <div><span class="text-muted-foreground truncate">{{ userEmail }}</span></div>
          </div>
          <Button variant="outline" size="sm" class="flex-shrink-0 ml-3" @click="handleLogout">Sign Out</Button>
        </div>

        <FormCreateOrg
          :open="showCreateOrgForm"
          @close="handleCloseForm"
          @submit="handleFormSubmit"
        />

        <div v-if="isLoading" class="flex justify-center py-8">
          <Spinner class="w-6 h-6" />
          <span class="ml-2 text-muted-foreground">Loading...</span>
        </div>

        <template v-if="dataReady && !isLoading">
          <div v-if="allInvites.length > 0" class="mb-6">
            <h2 class="text-base font-semibold text-foreground mb-2">Invitations</h2>
            <div class="border border-border rounded-lg overflow-hidden">
              <div
                v-for="item in allInvites"
                :key="item.key"
                @click="handleJoinInvite(item)"
                class="flex items-center justify-between p-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 transition-colors"
              >
                <div class="text-sm">
                  <h3 class="text-foreground">{{ item.name }}</h3>
                  <p class="text-muted-foreground">{{ item.typeLabel }}</p>
                </div>
                <Button variant="ghost" size="sm" class="text-muted-foreground hover:text-foreground">
                  <span>Join</span>
                  <ChevronRight class="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          <div class="mb-6">
            <h2 class="text-base font-semibold text-foreground mb-2">Your organizations</h2>
            <div v-if="displayOrganizations.length > 0" class="border border-border rounded-lg overflow-hidden">
              <div
                v-for="org in displayOrganizations"
                :key="org.id"
                @click="handleOpenOrganization(org)"
                class="flex items-center justify-between p-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 transition-colors"
              >
                <div class="text-sm">
                  <h3 class="text-foreground">{{ org.name }}</h3>
                </div>
                <Button variant="ghost" size="sm" class="text-muted-foreground hover:text-foreground">
                  <span>Open</span>
                  <ChevronRight class="w-5 h-5" />
                </Button>
              </div>
            </div>
            <p v-else class="text-sm text-muted-foreground">No organizations yet</p>
          </div>
        </template>

        <div class="text-center">
          <p class="text-sm text-muted-foreground">
            Not seeing your organization?<br>
            <a href="#" @click.prevent="handleLogout" class="text-primary hover:underline">Try a Different Account</a>
            or
            <a href="#" @click.prevent="handleCreateOrganization" class="text-primary hover:underline">Create a new one</a>
          </p>
        </div>

    </div>
  </SkylabPorthole>
</template>

<style>
.card-scroll::-webkit-scrollbar-track {
  margin: 16px 0;
  background: hsl(var(--card));
}
.card-scroll::-webkit-scrollbar {
  width: 6px;
}
.card-scroll::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}
</style>
